import * as React from "react";
import { Text } from "@uikit";

import {
  BREAKPOINTS,
  getCoursePricing,
  getPackageTotalCostForDate,
  Hotel,
  HotelExtended,
  Hotel_Package,
  roundTo,
  slugify,
} from "@kalabo/golfsavers-common";

import { isRegionalSite } from "@utilities/core";

import { Link } from "gatsby";
import { calcCrow } from "@kalabo/golfsavers-common";
import { Region, Coordinate } from "@kalabo/golfsavers-common";
import { ChevronRightIcon } from "@heroicons/react/outline";
import { useApp } from "@store/app";
import XE from "@components/common/xe";
import { FaMapMarkerAlt } from "@react-icons/all-files/fa/FaMapMarkerAlt";
import StarRating from "@components/starRating";
import { LazyLoadImage } from "react-lazy-load-image-component";
import useBreakpoint from "use-breakpoint";
import { useWindow } from "@hooks/useWindow";

interface iHotelsProps {
  hotels: Hotel[];
  region: Region;
  center: Coordinate;
  maxDistance?: number;
}

const OtherHotels = ({
  region,
  hotels,
  center,
  maxDistance = 10,
}: iHotelsProps) => {
  const date = useApp(($) => $.date);

  const win = useWindow();
  const { breakpoint } = useBreakpoint(BREAKPOINTS, "mobile");
  let current_region = region;
  const package_date = useApp((state) => state.package_date);
  const display_currency = useApp((state) => state.display_currency);

  if (!hotels) return null;

  const limit = ["desktop", "ultrawide"].includes(breakpoint) ? 8 : 6;

  const featured_hotels = hotels
    .map((a: any) => {
      let images = a.images;
      return {
        ...a,
        featured_image: images?.length > 0 ? images[0].url : null,
      };
    })
    .slice(0, limit);
  const renderSmall = React.useCallback(
    (pkg: Hotel_Package, hotel: any, index: any) => {
      return (
        <Link
          to={
            isRegionalSite()
              ? `/${slugify(
                  current_region.name.en.toLowerCase()
                )}-golf-packages/${hotel.slug}`
              : `/${slugify(
                  current_region.country.slug
                )}-golf-packages/${slugify(
                  current_region.name.en.toLowerCase()
                )}-golf-holidays/${hotel.slug}`
          }
          className={`bg-white rounded-[6px] relative flex flex-col col-span-12 sm:col-span-4 lg:col-span-3 shadow-card   overflow-hidden
  ${index > 6 && "sm:hidden lg:block"} `}
        >
          <div className={`aspect-[5/3] relative`}>
            <div className="hover:bg-[rgba(0,0,0,0.2)] transition-all  absolute inset-0"></div>
            <img
              src={`${process.env.GATSBY_IMAGE_PREFIX}${hotel.featured_image}?aspect_ratio=5:3&quality=70&width=300`}
              alt={`${hotel.name.en} golf course`}
              className="w-full"
            />
          </div>
          <div className=" flex flex-col gap-1  px-3 py-4">
            <Text
              size={15}
              weight="semibold"
              color="black90"
              leading={1.3}
              className="text-ellipsis overflow-hidden w-full whitespace-nowrap"
              element="h4"
            >
              {hotel.name.en}
            </Text>

            <div className="flex items-center gap-2 py-1">
              <StarRating
                hideCount
                hideEmpty
                color="green"
                rating={hotel.star_rating}
                size="small"
              />
              <div className="sm:block bg-grey3 h-[18px] w-[1px] border-grey2"></div>
              <Text
                sizeTablet={13}
                sizeDesktop={13}
                size={13}
                color="black80"
                leading={1.3}
                className="block  overflow-hidden"
              >
                <div className="flex gap-1 items-center">
                  <FaMapMarkerAlt className="w-4 h-4 text-grey6" />
                  <span className=" text-ellipsis overflow-hidden w-full whitespace-nowrap">
                    {hotel.area?.name?.en}
                  </span>
                </div>
              </Text>
            </div>
          </div>

          <div className="flex justify-between  border-t border-grey3 mx-3 py-4">
            <div className="flex flex-col flex-1 gap-[6px] overflow-hidden">
              <Text
                size={14}
                weight="semibold"
                color="black90"
                leading={1.2}
                className="text-ellipsis overflow-hidden w-full- whitespace-nowrap"
              >
                {breakpoint === "ultrawide" ? (
                  <>
                    {pkg?.number_of_nights} night
                    {pkg?.number_of_nights > 1 ? "s" : ""} /{" "}
                    {pkg?.courses?.length} round
                    {pkg?.courses?.length > 1 ? "s" : ""}
                  </>
                ) : (
                  <>
                    {pkg?.number_of_nights}N / {pkg?.courses?.length} round
                    {pkg?.courses?.length > 1 ? "s" : ""}
                  </>
                )}
              </Text>

              <Text
                size={11}
                color="black60"
                leading={1.3}
                className="text-ellipsis overflow-hidden w-full- whitespace-nowrap"
              >
                {pkg?.course_combo}
              </Text>
            </div>
            <div className="flex flex-col flex-1  gap-[6px] items-end">
              <div className="flex items-end gap-1">
                <Text size={9} color="black60" leading={1.2}>
                  from
                </Text>
                <Text
                  size={14}
                  color={"black90"}
                  weight="semibold"
                  leading={1.2}
                >
                  <XE
                    base_currency={pkg?.currency_code}
                    currency={display_currency.code}
                    hide_decimal
                    simplify={true}
                    value={roundTo(
                      getPackageTotalCostForDate(package_date, pkg),
                      0
                    )}
                  />
                </Text>
              </div>

              <Text size={11} color="black60" leading={1}>
                per person
              </Text>
            </div>
          </div>
        </Link>
      );
    },
    [breakpoint, display_currency, current_region]
  );

  const getListing = () => {
    return (
      <>
        {featured_hotels.map((hotel: Hotel, index: number) => {
          // get random package

          let randon_pkg = Math.floor(
            Math.random() * Math.floor(hotel?.packages.length)
          );

          let pkg =
            index === 0
              ? hotel?.packages.sort(
                  (a: any, b: any) =>
                    getPackageTotalCostForDate(package_date, a) -
                    getPackageTotalCostForDate(package_date, b)
                )[0]
              : hotel?.packages[randon_pkg];

          return (
            <React.Fragment key={index}>
              {renderSmall(pkg, hotel, index)}
            </React.Fragment>
          );
        })}
      </>
    );
  };
  //   .slice(0, 6);

  if (hotels.length === 0) return null;

  return (
    <div className="w-full  py-12  flex justify-center items-center relative bg-grey">
      <div className="lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-[20px] md:px-10 lg:px-0  flex flex-col justify-between w-full mb-12">
        <div className="mb-10 flex-col flex flex-1 overflow-hidden  pt-10">
          <div className="flex justify-between items-center mb-6">
            <Text
              size={21}
              element="h2"
              weight="semibold"
              color="black"
              leading={1.3}
            >
              Other {region.name.en} Golf & Hotel Packages You May Like
            </Text>
            <Link
              to={
                isRegionalSite()
                  ? `/${region.slug}-golf-packages`
                  : `/${slugify(region.country.slug)}/${slugify(
                      region.name.en.toLowerCase()
                    )}-golf-packages/`
              }
              className="hidden sm:block"
            >
              <Text
                size={15}
                weight="semibold"
                color="black"
                className="flex items-center hover:text-blue underline"
              >
                See all{" "}
                <span>
                  <ChevronRightIcon className="w-4" />
                </span>
              </Text>
            </Link>
          </div>
          <div className="grid grid-cols-12  gap-4 mb-4">{getListing()}</div>
        </div>
      </div>
    </div>
  );
};

export default OtherHotels;
